// pages/dashboard/SocialFeed.jsx - Cleaned up version with centralized middleware
"use client";

import { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  postsAPI,
  likesAPI,
  socialUtils,
  handleApiError,
} from "../../services/api";
import api from "../../services/api";
import { getUserPlaceholderImage } from "../../utils/imageUtils";
import { getUserTypeColor } from "../../utils/userTypeUtils";

// Import components
import StoriesCarousel from "../../components/social/stories/StoriesCarousel";
import PostFeed from "../../components/social/posts/PostFeed";
import SocialSidebar from "../../components/social/layout/SocialSidebar";
import RightSidebar from "../../components/social/layout/RightSidebar";
import DeleteConfirmationModal from "../../components/modals/DeleteConfirmation";
import PostModal from "../../components/modals/PostModal";
import { useTranslation } from "react-i18next";
import i18n from "../../i18n";
import SocialFeedSkeleton from "../../components/social/layout/SocialFeedSkeleton";
import { AnnouncementModal } from "../../components/modals";

// NOTE: Authentication and route protection is now handled centrally
// by RouteMiddleware in the App component. No need for individual
// authentication checks in this component.
import { useAuth } from "../../hooks/useAuth";
import { usePostUploadActions } from "../../contexts/PostUploadContext";
import "./SocialStyling.css";

const SocialFeed = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();

  const [posts, setPosts] = useState([]);
  const [stories, setStories] = useState([]);
  const [suggestedConnections, setSuggestedConnections] = useState([]);
  const [announcement, setAnnouncement] = useState(null);
  const [isAnnouncementModalOpen, setIsAnnouncementModalOpen] = useState(false);
  
  const [viewedStoryIds, setViewedStoryIds] = useState(new Set()); // Track viewed story IDs
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingStories, setIsLoadingStories] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMorePosts, setHasMorePosts] = useState(true);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);

  // Add timestamp to prevent duplicate calls
  const lastCallTimeRef = useRef(0);

  // Post modal state
  const [isPostModalOpen, setIsPostModalOpen] = useState(false);
  const [editingPostData, setEditingPostData] = useState(null);
  const [selectedPrivacy, setSelectedPrivacy] = useState("everyone");
  const [showPrivacyDropdown, setShowPrivacyDropdown] = useState(false);
  const [pendingFileType, setPendingFileType] = useState(null); // "image" or "video"
  const [isProcessingVideo, setIsProcessingVideo] = useState(false); // Video processing state
  const privacyDropdownRef = useRef(null);

  // Delete confirmation modal state
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [postToDelete, setPostToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // FIXED: Use refs to prevent multiple simultaneous API calls
  const isLoadingRef = useRef(false);
  const isLoadingStoriesRef = useRef(false);
  const loadedOnceRef = useRef(false);
  const storiesLoadedOnceRef = useRef(false);
  const mountedRef = useRef(true);
  const redirectedRef = useRef(false);
  const initialLoadTriggeredRef = useRef(false);

  // UNIFIED: Use single authentication hook
  const {
    user,
    isAuthenticated,
    isLoading: authLoading,
    getUserType,
    getDisplayName,
    getProfileImageUrl,
    updatePostsCount,
    // eslint-disable-next-line no-unused-vars
    getInitials,
  } = useAuth();

  const { startUpload, startUpdate } = usePostUploadActions();

  // Cleanup on unmount
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  // Listen for background upload completion to update the feed in real-time
  useEffect(() => {
    const handlePostUploadCompleted = (event) => {
      const { post } = event.detail;
      if (post) {
        // Transform backend data to frontend format
        const transformedPost = socialUtils.transformPostData(post);
        if (!transformedPost) return;

        setPosts((prevPosts) => {
          // Prevent duplicates if by any chance the post is already there
          if (prevPosts.some((p) => p.id === transformedPost.id)) return prevPosts;
          return [transformedPost, ...prevPosts];
        });

        // Update successful post count internally if available
        if (updatePostsCount) {
          updatePostsCount();
        }

        // Show a brief success toast
        setSuccessMessage(t("socialFeed.success.postCreated") || "Post successfully added to feed!");
        setTimeout(() => {
          if (mountedRef.current) setSuccessMessage(null);
        }, 5000);
      }
    };

    const handlePostUpdateCompleted = (event) => {
      const { post, postId, postData } = event.detail;
      if (post) {
        const transformedPost = socialUtils.transformPostData(post);
        if (!transformedPost) return;

        const isInvalidTimestamp =
          !transformedPost.timestamp ||
          String(transformedPost.timestamp).toLowerCase().includes("invalid");

        setPosts((prevPosts) =>
          prevPosts.map((p) => {
            const matches =
              String(p.id) === String(postId) || String(p.id) === String(post.id);
            if (!matches) return p;

            // Merge instead of replace: backend update responses are sometimes partial
            const merged = {
              ...p,
              ...transformedPost,
              author: {
                ...(p.author || {}),
                ...(transformedPost.author || {}),
              },
              reactions: {
                ...(p.reactions || {}),
                ...(transformedPost.reactions || {}),
              },
            };

            // Preserve author if transform couldn't derive it (common on partial responses)
            if (!transformedPost.author?.id) {
              merged.author = p.author;
            }

            // Preserve timestamp if transform produced invalid date
            if (isInvalidTimestamp) {
              merged.timestamp = p.timestamp;
            }

            // If backend didn't return content, fall back to submitted postData
            const submittedContent =
              typeof postData?.content === "string" ? postData.content : null;
            if (
              (!transformedPost.content || transformedPost.content.trim() === "") &&
              submittedContent &&
              submittedContent.trim() !== ""
            ) {
              merged.content = submittedContent;
            }

            // If backend didn't return media array, preserve existing media
            if (
              (!Array.isArray(transformedPost.media) ||
                transformedPost.media.length === 0) &&
              Array.isArray(p.media) &&
              p.media.length > 0
            ) {
              merged.media = p.media;
              merged.image = p.image;
            }

            return merged;
          })
        );

        setSuccessMessage(t("socialFeed.success.postUpdated") || "Post updated successfully!");
        setTimeout(() => {
          if (mountedRef.current) setSuccessMessage(null);
        }, 3000);
      }
    };

    window.addEventListener("post-upload-completed", handlePostUploadCompleted);
    window.addEventListener("post-update-completed", handlePostUpdateCompleted);
    return () => {
      window.removeEventListener("post-upload-completed", handlePostUploadCompleted);
      window.removeEventListener("post-update-completed", handlePostUpdateCompleted);
    };
  }, [updatePostsCount, t]);

  // Check for edit post data from profile pages (stored in sessionStorage)
  useEffect(() => {
    const editPostDataStr = sessionStorage.getItem('editPostData');
    if (editPostDataStr) {
      try {
        const post = JSON.parse(editPostDataStr);
        if (post) {
          setEditingPostData(post);
          setPendingFileType(null);
          setIsPostModalOpen(true);
          // Clear the sessionStorage after using it
          sessionStorage.removeItem('editPostData');
        }
      } catch (error) {
        console.error('Error parsing edit post data:', error);
        sessionStorage.removeItem('editPostData');
      }
    }
  }, []);



  // Authentication is now handled by RouteMiddleware

  // Posts count is now managed via user profile data (no need for separate API call)

  // FIXED: Get current user data with better fallbacks - made reactive to user changes
  const currentUser = useMemo(() => {
    // Use posts count from user data (already fetched by SocialSidebar via user profile API)
    const postsCount = user?.stats?.posts || user?.posts_count || 0;

    if (!user) {
      return {
        id: 1,
        name: "Loading User",
        username: "@loading",
        profile_image_url: "/icons/person.png",
        avatar: "/icons/person.png",
        role: "Pet Guardian",
        stats: {
          posts: 0,
          friends: 0,
          following: 0,
          followers: 0,
        },
        friends_count: 0,
        following_count: 0,
        followers_count: 0,
      };
    }

    const baseName = getDisplayName();
    const profileImage =
      user.profile_image_url ||
      user.profile_image ||
      user.avatar ||
      user.image ||
      getProfileImageUrl() ||
      getUserPlaceholderImage(user);

    const friendsCount =
      user.friends_count ??
      user.stats?.friends ??
      0;

    return {
      id: user.id || user.user_id,
      name: baseName,
      username:
        user.username ||
        `@${baseName.replace(/\s+/g, "").toLowerCase()}`,
      profile_image_url: profileImage,
      avatar: profileImage,
      role:
        getUserType()?.replace(/\b\w/g, (l) => l.toUpperCase()) ||
        "Pet Guardian",
      sub_type: user.sub_type || user.subType,
      sub_type_display_name: user.sub_type_display_name || user.subTypeDisplayName,
      stats: {
        posts: postsCount,
        friends: friendsCount,
        following: user.following_count || 0,
        followers: user.followers_count || 0,
      },
      friends_count: friendsCount,
      following_count: user.following_count || 0,
      followers_count: user.followers_count || 0,
      average_rating: user.average_rating ?? user.averageRating ?? 0,
      ratings_count: user.ratings_count ?? user.ratingsCount ?? 0,
      rating_label: user.rating_label ?? user.ratingLabel ?? null,
    };
  }, [user, getDisplayName, getProfileImageUrl, getUserType]);

  // Helper function to initialize viewed story IDs from backend data
  const initializeViewedStoryIds = (storiesData) => {
    const viewedIds = new Set();
    if (storiesData && storiesData.length > 0) {
      storiesData.forEach((storyUser) => {
        if (storyUser.stories && storyUser.stories.length > 0) {
          storyUser.stories.forEach((story) => {
            // Check if story is marked as viewed by backend
            if (story.has_viewed || story.is_viewed) {
              // Use same ID format as processed stories (story.id)
              const storyId = story.id || story.story_id;
              if (storyId) {
                // Convert to string for consistent Set comparison
                viewedIds.add(String(storyId));
              }
            }
          });
        }
      });
    }
    return viewedIds;
  };

  // Helper function to process grouped stories
  const processGroupedStories = (storiesData) => {
    const groupedStories = [];

    if (storiesData && storiesData.length > 0) {
      storiesData.forEach((storyUser) => {
        // Skip if no stories
        if (!storyUser.stories || storyUser.stories.length === 0) return;

        const userInfo = {
          id: storyUser.user_id,
          name: storyUser.name,
          avatar: storyUser.profile_image || getUserPlaceholderImage({ name: storyUser.name }),
          isAddStory: false,
        };

        const userStories = storyUser.stories.map((story, index) => {
          // Extract created_at from story or its first media item
          const storyCreatedAt = story.created_at || (story.media && story.media[0]?.created_at);

          const baseStoryData = {
            user: userInfo,
            timestamp: storyCreatedAt
              ? socialUtils.formatTimestamp(storyCreatedAt)
              : "Just now",
            rawCreatedAt: storyCreatedAt, // Save raw date for reliable comparison
            content: story.caption || "",
            expiresAt: story.expires_at,
            isExpired: story.is_expired, // Note: check if these are returned by API now
            isActive: true, // Assuming active since fetched
            timeRemaining: story.time_remaining,
          };

          // Process media for each story
          let processedStory = {
            id: story.id,
            storyId: story.id,
            has_viewed: story.has_viewed || story.is_viewed || false, // Preserve viewed status from backend
            ...baseStoryData
          };

          // Determine media type and url
          if (story.media && Array.isArray(story.media) && story.media.length > 0) {
            const mediaItem = story.media[0]; // Take first media for preview
            // Use thumbnail/poster for video stories in the carousel
            processedStory.image = mediaItem.file_type === 'video'
              ? (mediaItem.thumbnail || mediaItem.thumbnail_url || mediaItem.poster)
              : mediaItem.url;
            processedStory.video = mediaItem.file_type === 'video' ? mediaItem.url : null;
            processedStory.type = mediaItem.file_type || 'image';
            // Also preserve thumbnail for StoryViewer/StoryMedia
            processedStory.thumbnail_url = mediaItem.thumbnail || mediaItem.thumbnail_url || mediaItem.poster;
          } else {
            const mediaUrl = story.media_url;
            const isVideo = mediaUrl && (mediaUrl.includes('.mp4') || mediaUrl.includes('.webm') || mediaUrl.includes('.mov'));
            processedStory.image = isVideo ? null : mediaUrl;
            processedStory.video = isVideo ? mediaUrl : null;
            processedStory.type = isVideo ? 'video' : 'image';
          }
          return processedStory;
        });

        // Sort stories from oldest to newest (ascending by created_at)
        userStories.sort((a, b) => {
          const dateA = a.rawCreatedAt ? new Date(a.rawCreatedAt) : new Date(0);
          const dateB = b.rawCreatedAt ? new Date(b.rawCreatedAt) : new Date(0);
          return dateA.getTime() - dateB.getTime(); // Ascending order (oldest first)
        });

        // Find latest story for the ring border/background using raw timestamps
        const latestStory = userStories.reduce((latest, current) => {
          if (!latest) return current;
          const dateCurrent = current.rawCreatedAt ? new Date(current.rawCreatedAt) : new Date(0);
          const dateLatest = latest.rawCreatedAt ? new Date(latest.rawCreatedAt) : new Date(0);
          return dateCurrent > dateLatest ? current : latest;
        }, userStories[0]);

        groupedStories.push({
          id: `group-${storyUser.user_id}`,
          user: userInfo,
          stories: userStories,
          latestStory: latestStory,
          storyCount: userStories.length
        });
      });
    }

    return groupedStories;
  };

  // Load consolidated feed data (posts, stories, suggested connections)
  const loadConsolidatedFeed = async (page = 1, append = false) => {
    // CRITICAL: Prevent multiple simultaneous calls
    // BUT: Allow initial load even if isLoadingRef is true (might be stuck)
    const isInitialLoad = !append && !loadedOnceRef.current;
    if ((isLoadingRef.current && !isInitialLoad) || !mountedRef.current) {
      return;
    }

    // Prevent duplicate calls within 1 second
    // BUT: Allow initial load to bypass this check
    const now = Date.now();
    if (!isInitialLoad && now - lastCallTimeRef.current < 1000) {
      return;
    }
    lastCallTimeRef.current = now;

    // Check authentication
    const token = localStorage.getItem("access_token");
    const userData = localStorage.getItem("user_data");

    if (
      !token ||
      !userData ||
      userData === "undefined" ||
      userData === "null"
    ) {
      if (!redirectedRef.current) {
        redirectedRef.current = true;
        navigate("/auth/login", { replace: true });
      }
      return;
    }

    try {
      isLoadingRef.current = true;
      setError(null);

      // Only set loading state if this is the initial load or no stories yet
      if (!append && mountedRef.current) {
        setIsLoading(true);
        if (stories.length === 0) {
          setIsLoadingStories(true);
        }
      }

      // On initial load, load only first page - more will load as user scrolls
      if (isInitialLoad) {
        let allStoriesData = [];
        let allSuggestedConnections = [];

        // Load first page with reasonable size for fast initial render
        const perPage = 20; // Load 20 posts per page

        // Fetch first page immediately for fast initial load
        // Fetch first page immediately for fast initial load
        const firstPageResponse = await postsAPI.getConsolidatedFeed({
          postPage: 1, // Use postPage instead of page
          storyPage: 1,
          per_page: perPage
        });

        if (!mountedRef.current) {
          return;
        }

        const firstPageData = firstPageResponse.data;

        if (firstPageData.status === "success" && firstPageData.data) {
          const { posts, stories, suggested_connections, announcement } = firstPageData.data;

          // Handle System Announcement (if any)
          if (announcement && announcement.id) {
            const lastSeenId = localStorage.getItem('last_seen_announcement_id');
            if (lastSeenId !== announcement.id) {
              setAnnouncement(announcement);
              setIsAnnouncementModalOpen(true);
              localStorage.setItem('last_seen_announcement_id', announcement.id);
            }
          }

          // Collect first page posts
          let allPostsData = [];
          if (posts && posts.data && Array.isArray(posts.data)) {
            allPostsData = [...posts.data];
          }

          // Collect stories (from first page)
          if (stories && stories.data) {
            allStoriesData = stories.data;
          }

          // Collect suggested connections (from first page)
          if (suggested_connections) {
            allSuggestedConnections = suggested_connections;
          }

          // Get total pages info and store for scroll-based loading
          const totalPages = posts?.pagination?.last_page ||
            posts?.last_page ||
            posts?.meta?.last_page ||
            1;

          // Update pagination state
          setCurrentPage(1);
          setHasMorePosts(totalPages > 1);

          // Helper function to process and set posts
          const processAndSetPosts = (postsData) => {
            if (postsData.length === 0) return [];

            const transformedPosts = postsData
              .map((originalPost) => {
                const transformed = socialUtils.transformPostData(originalPost);
                if (transformed) {
                  transformed._originalPost = originalPost;
                  // Ensure status is preserved for processing check
                  if (originalPost.status) {
                    transformed.status = originalPost.status;
                  }
                }
                return transformed;
              })
              .filter((post) => {
                if (post === null) return false;
                if (post.channel && post._originalPost) {
                  const originalPost = post._originalPost;
                  const membershipIndicators = [
                    originalPost.channel?.is_joined,
                    originalPost.channel?.isJoined,
                    originalPost.channel?.is_member,
                    originalPost.channel?.isMember,
                    originalPost.channel?.user_is_member,
                    originalPost.channel?.userIsMember,
                    originalPost.is_channel_member,
                    originalPost.isChannelMember,
                    originalPost.channel_member,
                    originalPost.channelMember,
                  ];
                  const isMember = membershipIndicators.some(val => val === true);
                  if (!isMember) return false;
                }
                return true;
              })
              .map((post) => {
                if (post && post._originalPost) {
                  const { _originalPost, ...cleanPost } = post;
                  return cleanPost;
                }
                return post;
              })
              .sort((a, b) => {
                const dateA = new Date(a.created_at || a.timestamp);
                const dateB = new Date(b.created_at || b.timestamp);
                return dateB - dateA;
              });

            // Check if any post is processing to show the banner
            const hasProcessingPost = transformedPosts.some(p => p && p.status === 'processing');
            if (hasProcessingPost) {
              setIsProcessingVideo(true);
            }

            return transformedPosts.filter(p => p !== null && p !== undefined);
          };

          // Process and set first page immediately
          const firstPagePosts = processAndSetPosts(allPostsData);
          if (firstPagePosts.length > 0) {
            setPosts(firstPagePosts);
          } else if (user?.id && !loadedOnceRef.current) {
            // Fallback to user's own posts if consolidated feed is empty
            const fetchAllUserPosts = async () => {
              try {
                let allUserPosts = [];
                let currentPage = 1;
                let hasMorePages = true;
                const perPage = 50;

                while (hasMorePages && currentPage <= 5) {
                  const myPostsResponse = await postsAPI.getMyPosts({
                    user_id: user.id,
                    page: currentPage,
                    per_page: perPage
                  });

                  if (!mountedRef.current) return;

                  const myPostsData = myPostsResponse.data;
                  let pagePosts = [];

                  if (myPostsData?.data) {
                    if (Array.isArray(myPostsData.data)) {
                      pagePosts = myPostsData.data;
                    } else if (myPostsData.data.data && Array.isArray(myPostsData.data.data)) {
                      pagePosts = myPostsData.data.data;
                    }
                  } else if (Array.isArray(myPostsData)) {
                    pagePosts = myPostsData;
                  }

                  allUserPosts = [...allUserPosts, ...pagePosts];

                  const totalPages = myPostsData?.meta?.last_page ||
                    myPostsData?.last_page ||
                    myPostsData?.data?.last_page ||
                    1;

                  hasMorePages = pagePosts.length === perPage && currentPage < totalPages;
                  currentPage++;
                }

                if (!mountedRef.current) return;
                if (loadedOnceRef.current) return;

                const transformedMyPosts = processAndSetPosts(allUserPosts);
                setPosts((currentPosts) => {
                  if (Array.isArray(currentPosts) && currentPosts.length > 0) {
                    const existingIds = new Set(currentPosts.map(p => String(p.id)));
                    const newPosts = transformedMyPosts.filter(p => !existingIds.has(String(p.id)));
                    if (newPosts.length > 0) {
                      const combined = [...currentPosts, ...newPosts].sort((a, b) => {
                        const dateA = new Date(a.created_at || a.timestamp);
                        const dateB = new Date(b.created_at || b.timestamp);
                        return dateB - dateA;
                      });
                      return combined;
                    }
                    return currentPosts;
                  }
                  return transformedMyPosts.length > 0 ? transformedMyPosts : [];
                });

                setHasMorePosts(false);
                setCurrentPage(1);
              } catch (error) {
                console.warn('SocialFeed: Failed to fetch user own posts:', error);
                if (mountedRef.current) {
                  setPosts([]);
                  setHasMorePosts(false);
                }
              }
            };

            fetchAllUserPosts();
          }
        } else {
          // If first page failed, set error
          setError(t("socialFeed.errors.loadFailed"));
        }

        // Process stories (from first page only)
        // Always set stories, even if empty (to show "Add Story" option)
        if (allStoriesData.length > 0) {
          // Initialize viewed story IDs from backend data
          const backendViewedIds = initializeViewedStoryIds(allStoriesData);
          setViewedStoryIds(backendViewedIds);

          // Process grouped stories from API
          const groupedStories = processGroupedStories(allStoriesData);

          const storiesWithAddOption = [
            {
              id: 0,
              user: {
                name: t("postFeed.addStory"),
                avatar: "/icons/person.png",
                isAddStory: true,
              },
            },
            ...groupedStories,
          ];

          setStories(storiesWithAddOption);
          storiesLoadedOnceRef.current = true;
        } else {
          // No stories from API, but still show "Add Story" option
          const storiesWithAddOption = [
            {
              id: 0,
              user: {
                name: t("postFeed.addStory"),
                avatar: "/icons/person.png",
                isAddStory: true,
              },
            },
          ];
          setStories(storiesWithAddOption);
          storiesLoadedOnceRef.current = true;
        }

        // Process suggested connections (from first page only)
        if (allSuggestedConnections.length > 0) {
          const filteredConnections = allSuggestedConnections.filter(
            connection =>
              connection.user_type !== 'ADMIN' &&
              connection.user_type !== 'admin' &&
              connection.user_type !== 'CLINIC_MODERATOR' &&
              connection.user_type !== 'clinic_moderator'
          );

          const transformedConnections = filteredConnections.map(
            (connection) => ({
              id: connection.id || connection.user_id,
              user_id: connection.user_id || connection.id,
              name:
                `${connection.first_name || ""} ${connection.last_name || ""
                  }`.trim() ||
                connection.username,
              avatar: (() => {
                if (connection.profile_image_url && !connection.profile_image_url.includes('placeholder')) {
                  return connection.profile_image_url;
                }
                if (connection.avatar && !connection.avatar.includes('placeholder')) {
                  return connection.avatar;
                }
                return getUserPlaceholderImage(connection);
              })(),
              mutualConnections: connection.mutual_connections_count || 0,
              company: connection.user_type || "Pet Guardian",
              username: connection.username || `@${connection.id || connection.user_id}`,
            })
          );

          setSuggestedConnections(transformedConnections);
        } else {
          setSuggestedConnections([]);
        }

        loadedOnceRef.current = true;

        if (mountedRef.current) {
          setIsLoading(false);
          setIsLoadingStories(false);
        }
        isLoadingRef.current = false;
        return; // Exit early for initial load
      }

      // For non-initial loads (append/refresh), use the original logic but with postPage
      const response = await postsAPI.getConsolidatedFeed({
        postPage: page,
        per_page: 20
      });

      const data = response.data;
      if (!mountedRef.current) {
        return;
      }


      if (data.status === "success" && data.data) {
        const { posts, stories, suggested_connections, announcement } = data.data;

        // Handle System Announcement (if any)
        if (announcement && announcement.id) {
          const lastSeenId = localStorage.getItem('last_seen_announcement_id');
          if (lastSeenId !== announcement.id) {
            setAnnouncement(announcement);
            setIsAnnouncementModalOpen(true);
            localStorage.setItem('last_seen_announcement_id', announcement.id);
          }
        }

        // Handle posts data
        if (posts && (posts.data || Array.isArray(posts))) {
          // Handle both posts.data array and direct posts array
          const postsArray = Array.isArray(posts.data) ? posts.data : (Array.isArray(posts) ? posts : []);

          // First transform all posts, then filter
          const transformedPosts = postsArray
            .map((originalPost, index) => {
              const transformed = socialUtils.transformPostData(originalPost);
              // Attach original post data for membership checking
              if (transformed) {
                transformed._originalPost = originalPost;
              }
              return transformed;
            })
            .filter((post) => {
              // Filter out null posts
              if (post === null) return false;

              // If post is from a channel, only show it if user is confirmed to be a member
              if (post.channel && post._originalPost) {
                const originalPost = post._originalPost;

                // Check various membership indicators from backend
                const membershipIndicators = [
                  originalPost.channel?.is_joined,
                  originalPost.channel?.isJoined,
                  originalPost.channel?.is_member,
                  originalPost.channel?.isMember,
                  originalPost.channel?.user_is_member,
                  originalPost.channel?.userIsMember,
                  originalPost.is_channel_member,
                  originalPost.isChannelMember,
                  originalPost.channel_member,
                  originalPost.channelMember,
                ];

                // Check if user is explicitly marked as a member
                const isMember = membershipIndicators.some(val => val === true);

                // Only show channel posts if user is confirmed to be a member
                // If membership info is missing or false, hide the post for privacy
                if (!isMember) {
                  return false;
                }
              }

              return true;
            })
            .map((post) => {
              // Remove the temporary _originalPost property
              if (post && post._originalPost) {
                const { _originalPost, ...cleanPost } = post;
                return cleanPost;
              }
              return post;
            })
            .sort((a, b) => {
              // Sort by creation date, newest first
              const dateA = new Date(a.created_at || a.timestamp);
              const dateB = new Date(b.created_at || b.timestamp);
              return dateB - dateA;
            });


          if (append) {
            setPosts((prevPosts) => {
              // Ensure prevPosts is an array
              const prevPostsArray = Array.isArray(prevPosts) ? prevPosts : [];
              const combined = [...prevPostsArray, ...transformedPosts];
              // Sort the combined array to maintain proper order
              return combined.sort((a, b) => {
                const dateA = new Date(a.created_at || a.timestamp);
                const dateB = new Date(b.created_at || b.timestamp);
                return dateB - dateA;
              });
            });
          } else {
            // When replacing posts (not appending)
            // Use isInitialLoad flag to determine if we should directly set or merge
            if (isInitialLoad) {
              // Initial load - directly set posts (no merge needed)
              const validPosts = transformedPosts.filter(p => p !== null && p !== undefined);

              // If consolidated feed returns no posts on initial load, fetch user's own posts as fallback
              // CRITICAL: Only do this on true initial load AND only if loadedOnceRef is still false
              // This prevents the fallback from running if posts were already loaded or created
              if (validPosts.length === 0 && user?.id && !loadedOnceRef.current) {
                // Fetch ALL user's own posts (with pagination) to ensure we get all posts
                const fetchAllUserPosts = async () => {
                  try {
                    let allUserPosts = [];
                    let currentPage = 1;
                    let hasMorePages = true;
                    const perPage = 50;

                    // Keep fetching until we get all posts
                    while (hasMorePages && currentPage <= 5) { // Limit to 5 pages for safety
                      const myPostsResponse = await postsAPI.getMyPosts({
                        user_id: user.id,
                        page: currentPage,
                        per_page: perPage
                      });

                      if (!mountedRef.current) return;

                      const myPostsData = myPostsResponse.data;
                      let pagePosts = [];

                      // Handle different response structures
                      if (myPostsData?.data) {
                        if (Array.isArray(myPostsData.data)) {
                          pagePosts = myPostsData.data;
                        } else if (myPostsData.data.data && Array.isArray(myPostsData.data.data)) {
                          pagePosts = myPostsData.data.data;
                        }
                      } else if (Array.isArray(myPostsData)) {
                        pagePosts = myPostsData;
                      }

                      allUserPosts = [...allUserPosts, ...pagePosts];

                      // Check if there are more pages
                      const totalPages = myPostsData?.meta?.last_page ||
                        myPostsData?.last_page ||
                        myPostsData?.data?.last_page ||
                        1;

                      hasMorePages = pagePosts.length === perPage && currentPage < totalPages;
                      currentPage++;
                    }

                    if (!mountedRef.current) return;

                    // Double-check: Don't proceed if loadedOnceRef is now true (posts were loaded/created)
                    if (loadedOnceRef.current) {
                      return; // Posts already loaded, don't overwrite
                    }

                    // Transform all user's own posts
                    const transformedMyPosts = allUserPosts
                      .map((originalPost) => socialUtils.transformPostData(originalPost))
                      .filter((post) => post !== null && post !== undefined)
                      .sort((a, b) => {
                        const dateA = new Date(a.created_at || a.timestamp);
                        const dateB = new Date(b.created_at || b.timestamp);
                        return dateB - dateA;
                      });

                    // Only set posts if we don't already have posts (to preserve newly created posts)
                    // Since this is initial load, we should check the current posts state
                    // Use a ref to track if we should set posts (only if posts array is still empty)
                    setPosts((currentPosts) => {
                      // CRITICAL: Only set posts if the current posts array is empty
                      // This prevents overwriting posts that were created before the fallback completes
                      if (Array.isArray(currentPosts) && currentPosts.length > 0) {
                        // Posts already exist (e.g., from a newly created post), don't overwrite
                        // Instead, merge any missing posts
                        const existingIds = new Set(currentPosts.map(p => String(p.id)));
                        const newPosts = transformedMyPosts.filter(p => !existingIds.has(String(p.id)));
                        if (newPosts.length > 0) {
                          // Combine and sort by date
                          const combined = [...currentPosts, ...newPosts].sort((a, b) => {
                            const dateA = new Date(a.created_at || a.timestamp);
                            const dateB = new Date(b.created_at || b.timestamp);
                            return dateB - dateA;
                          });
                          return combined;
                        }
                        // No new posts to add, keep existing posts
                        return currentPosts;
                      }

                      // No existing posts, safe to set the fetched posts
                      return transformedMyPosts.length > 0 ? transformedMyPosts : [];
                    });
                  } catch (error) {
                    console.warn('SocialFeed: Failed to fetch user own posts:', error);
                    if (mountedRef.current) {
                      setPosts([]);
                    }
                  }
                };

                fetchAllUserPosts();
              } else {
                setPosts(validPosts);
              }
            } else {
              // Refresh - merge to preserve local reaction states
              setPosts((prevPosts) => {
                const prevPostsArray = Array.isArray(prevPosts) ? prevPosts : [];

                // CRITICAL: If transformedPosts is empty but we have existing posts, keep existing posts
                // This prevents clearing posts when the API returns empty results
                if (transformedPosts.length === 0 && prevPostsArray.length > 0) {
                  return prevPostsArray; // Keep existing posts, don't clear them
                }

                // If no previous posts, just set directly
                if (prevPostsArray.length === 0) {
                  return transformedPosts;
                }

                // Merge to preserve local reaction states
                // First, find posts that exist in transformedPosts but not in prevPosts (new posts from API)
                const existingIds = new Set(prevPostsArray.map(p => String(p.id)));
                const newPostsFromAPI = transformedPosts.filter(p => !existingIds.has(String(p.id)));

                // Then merge existing posts with updated data
                const mergedPosts = transformedPosts.map((newPost) => {
                  const existingPost = prevPostsArray.find(p => String(p.id) === String(newPost.id));

                  if (existingPost && existingPost.updated_at && newPost.updated_at) {
                    const existingTime = new Date(existingPost.updated_at).getTime();
                    const newTime = new Date(newPost.updated_at).getTime();

                    if (existingTime > newTime) {
                      return {
                        ...newPost,
                        user_reaction_type: existingPost.user_reaction_type,
                        reactions_count: existingPost.reactions_count,
                        reaction_counts: existingPost.reaction_counts,
                        reactions: existingPost.reactions,
                        updated_at: existingPost.updated_at,
                      };
                    }
                  }

                  return newPost;
                });

                // Combine merged posts with any new posts from API, and preserve any posts that weren't in the API response
                // (e.g., newly created posts that haven't been synced yet)
                const postsNotInAPI = prevPostsArray.filter(p =>
                  !transformedPosts.some(tp => String(tp.id) === String(p.id))
                );

                // Combine all posts and sort by date
                const allPosts = [...mergedPosts, ...postsNotInAPI, ...newPostsFromAPI];
                const uniquePosts = Array.from(
                  new Map(allPosts.map(p => [String(p.id), p])).values()
                );

                return uniquePosts.sort((a, b) => {
                  const dateA = new Date(a.created_at || a.timestamp);
                  const dateB = new Date(b.created_at || b.timestamp);
                  return dateB - dateA;
                });
              });
            }
          }

          // Update pagination info
          setHasMorePosts(
            posts.pagination
              ? posts.pagination.current_page < posts.pagination.last_page
              : false
          );
          setCurrentPage(
            posts.pagination ? posts.pagination.current_page : page
          );
        } else if (posts && Array.isArray(posts) && posts.length === 0) {
          // Handle case where posts is an empty array
          setPosts([]);
          setHasMorePosts(false);
          setCurrentPage(1);
        }

        // Handle stories data
        // Handle stories data
        if (stories && stories.data) {
          // Initialize viewed story IDs from backend data
          const backendViewedIds = initializeViewedStoryIds(stories.data);
          setViewedStoryIds(backendViewedIds);

          // Process grouped stories from API
          const groupedStories = processGroupedStories(stories.data);

          // Add the "Add Your Story" option at the beginning
          const storiesWithAddOption = [
            {
              id: 0,
              user: {
                name: t("postFeed.addStory"),
                avatar: "/icons/person.png",
                isAddStory: true,
              },
            },
            ...groupedStories,
          ];

          setStories(storiesWithAddOption);
          storiesLoadedOnceRef.current = true;
        } else {
          // No stories from API, but still show "Add Story" option
          const storiesWithAddOption = [
            {
              id: 0,
              user: {
                name: t("postFeed.addStory"),
                avatar: "/icons/person.png",
                isAddStory: true,
              },
            },
          ];
          setStories(storiesWithAddOption);
          storiesLoadedOnceRef.current = true;
        }

        // Handle suggested connections
        if (suggested_connections && Array.isArray(suggested_connections)) {
          // Filter out ADMIN and CLINIC_MODERATOR users from suggested connections
          // CLINIC_MODERATOR users should only appear in clinic team management
          const filteredConnections = suggested_connections.filter(
            connection =>
              connection.user_type !== 'ADMIN' &&
              connection.user_type !== 'admin' &&
              connection.user_type !== 'CLINIC_MODERATOR' &&
              connection.user_type !== 'clinic_moderator'
          );

          // Transform the suggested connections data to match the expected format
          const transformedConnections = filteredConnections.map(
            (connection) => ({
              id: connection.id || connection.user_id,
              user_id: connection.user_id || connection.id, // Add user_id for navigation
              name:
                `${connection.first_name || ""} ${connection.last_name || ""
                  }`.trim() ||
                connection.username,
              avatar: (() => {
                // Check if profile_image_url exists and is not a placeholder
                if (connection.profile_image_url && !connection.profile_image_url.includes('placeholder')) {
                  return connection.profile_image_url;
                }
                // Check if avatar exists and is not a placeholder
                if (connection.avatar && !connection.avatar.includes('placeholder')) {
                  return connection.avatar;
                }
                // Default fallback
                return getUserPlaceholderImage(connection);
              })(),
              mutualConnections: connection.mutual_connections_count || 0,
              company: connection.user_type || "Pet Guardian",
              username: connection.username || `@${connection.id || connection.user_id}`,
            })
          );

          setSuggestedConnections(transformedConnections);
        } else {
          setSuggestedConnections([]);
        }

        loadedOnceRef.current = true;
      } else {
        setError(t("socialFeed.errors.loadFailed"));
      }
    } catch (error) {

      if (!mountedRef.current) return;

      // Handle specific error types
      if (
        error.code === "ERR_NETWORK" ||
        error.message.includes("ERR_CONNECTION_REFUSED")
      ) {
        setError(t("socialFeed.errors.network"));
      } else if (error.response?.status === 401) {
        setError(t("socialFeed.errors.sessionExpired"));
        setTimeout(() => {
          if (!redirectedRef.current) {
            redirectedRef.current = true;
            navigate("/auth/login", { replace: true });
          }
        }, 2000);
      } else if (error.response?.status === 404) {
        setError(t("socialFeed.errors.notFound"));
      } else if (error.response?.status === 500) {
        setError(t("socialFeed.errors.server"));
      } else {
        setError(handleApiError(error));
      }
    } finally {
      if (mountedRef.current) {
        setIsLoading(false);
        setIsLoadingStories(false);
      }
      isLoadingRef.current = false;
    }
  };

  // Helper function to format timestamp
  const formatTimestamp = (timestamp) => {
    const now = new Date();
    const storyTime = new Date(timestamp);
    const diffInHours = Math.floor((now - storyTime) / (1000 * 60 * 60));

    if (diffInHours < 1) return t("socialFeed.timestamps.justNow");
    if (diffInHours < 24)
      return t("socialFeed.timestamps.hoursAgo", { count: diffInHours });

    const diffInDays = Math.floor(diffInHours / 24);
    if (diffInDays < 7)
      return t("socialFeed.timestamps.daysAgo", { count: diffInDays });

    const diffInWeeks = Math.floor(diffInDays / 7);
    if (diffInWeeks < 4)
      return t("socialFeed.timestamps.weeksAgo", { count: diffInWeeks });

    const locale = i18n.language || (document?.documentElement?.lang ?? "en");
    return t("socialFeed.timestamps.date", {
      date: storyTime.toLocaleDateString(locale, {
        year: "numeric",
        month: "short",
        day: "numeric",
      }),
    });
  };

  // Load initial consolidated feed - wait for auth to be ready
  useEffect(() => {
    // Wait for auth to finish loading before attempting to load posts
    if (authLoading) {
      return;
    }

    // Ensure we load posts on initial mount - only once
    if (mountedRef.current && !loadedOnceRef.current && !initialLoadTriggeredRef.current) {
      // Mark as triggered immediately to prevent duplicate calls
      initialLoadTriggeredRef.current = true;

      // Reset loading refs to allow initial load (force reset)
      isLoadingRef.current = false;
      lastCallTimeRef.current = 0; // Reset timestamp to allow immediate load

      // Use setTimeout to ensure state is ready
      const timer = setTimeout(() => {
        if (mountedRef.current && !loadedOnceRef.current) {
          loadConsolidatedFeed(1, false);
        }
      }, 100);

      return () => clearTimeout(timer);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authLoading]);


  // Update "Add Story" translation when language changes
  useEffect(() => {
    const handleLanguageChange = () => {
      setStories((prevStories) => {
        if (prevStories.length === 0) return prevStories;

        const updatedStories = [...prevStories];
        const addStoryIndex = updatedStories.findIndex(
          (story) => story.user?.isAddStory
        );

        if (addStoryIndex !== -1) {
          updatedStories[addStoryIndex] = {
            ...updatedStories[addStoryIndex],
            user: {
              ...updatedStories[addStoryIndex].user,
              name: t("postFeed.addStory"),
            },
          };
        }
        return updatedStories;
      });
    };

    // Listen for language changes
    i18n.on("languageChanged", handleLanguageChange);

    // Cleanup
    return () => {
      i18n.off("languageChanged", handleLanguageChange);
    };
  }, [t]);

  // Event listeners for scroll, focus, and video processing
  useEffect(() => {
    const handleScroll = () => {
      // Placeholder for scroll handling logic, if any
    };

    const handleFocus = () => {
      // Placeholder for focus handling logic, if any
    };

    const handleVideoProcessed = (event) => {
      console.log('📱 [SocialFeed] Video completion event received:', event.detail);

      const { post_id, media_id } = event.detail;

      if (!post_id) {
        console.warn('⚠️ [SocialFeed] No post_id in event, cannot update');
        return;
      }

      // Strategy 1: Update existing post in state if found
      setPosts(prevPosts => {
        const postIndex = prevPosts.findIndex(p => p.id === post_id);

        if (postIndex !== -1) {
          console.log('✅ [SocialFeed] Found post in state, updating:', post_id);
          const updatedPosts = [...prevPosts];
          updatedPosts[postIndex] = {
            ...updatedPosts[postIndex],
            status: 'published',
            media: updatedPosts[postIndex].media.map(m =>
              m.id === media_id ? { ...m, ...event.detail } : m
            )
          };
          return updatedPosts;
        } else {
          console.log('ℹ️ [SocialFeed] Post not in state, will reload feed');
          return prevPosts;
        }
      });

      // Strategy 2: Clear processing flag
      setIsProcessingVideo(false);

      // Strategy 3: Trigger full feed reload (ensures latest data)
      console.log('🔄 [SocialFeed] Reloading consolidated feed...');
      setSuccessMessage('Video processed successfully! Refreshing feed...');
      setTimeout(() => {
        setSuccessMessage('');
        loadConsolidatedFeed(true);
      }, 2000);
    };

    window.addEventListener('scroll', handleScroll);
    window.addEventListener('focus', handleFocus);
    window.addEventListener('video-processing-completed', handleVideoProcessed);

    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('focus', handleFocus);
      window.removeEventListener('video-processing-completed', handleVideoProcessed);
    };
  }, [setIsProcessingVideo, setSuccessMessage, loadConsolidatedFeed]); // Dependencies for the effect // Include t to get updated translation function

  // Handle creating new post (Non-blocking)
  const handleCreatePost = (newPostData) => {
    try {
      if (!mountedRef.current) return;
      setError(null);

      // We no longer await the API call here. 
      // It's handed off to the background context.
      startUpload(newPostData);

      // Close modal immediately
      closePostModal();

      // Show a brief success message that upload has started
      setSuccessMessage(t("socialFeed.success.uploadStarted"));
      setTimeout(() => {
        if (mountedRef.current) setSuccessMessage(null);
      }, 3000);

      // Optional: If we knew it was a video, we could still show the banner
      // but the context might be better for that.
    } catch (err) {
      console.error("Failed to initiate post creation:", err);
      if (mountedRef.current) {
        setError(t("socialFeed.errors.createPostInitiation") || "Failed to start post upload");
      }
    }
  };

  const handleUpdatePost = (postId, updatedData) => {
    try {
      if (!mountedRef.current) return;
      setError(null);

      // Hand off to background upload
      startUpdate(postId, updatedData);

      // Optimistic local update (so user sees changes immediately)
      setPosts((prevPosts) =>
        prevPosts.map((p) => {
          if (String(p.id) !== String(postId)) return p;

          const next = { ...p };
          if (typeof updatedData?.content === "string") {
            next.content = updatedData.content;
          }
          if (typeof updatedData?.privacy === "string") {
            next.privacy = updatedData.privacy;
          }
          if (Array.isArray(updatedData?.removedMediaIds) && Array.isArray(next.media)) {
            const removed = new Set(updatedData.removedMediaIds.map((id) => String(id)));
            next.media = next.media.filter((m) => !removed.has(String(m?.id)));
            next.image = next.media?.[0]?.url || next.image;
          }
          return next;
        })
      );

      // Close modal immediately
      closePostModal();

      // Show brief info message
      setSuccessMessage(t("socialFeed.success.updateStarted"));
      setTimeout(() => {
        if (mountedRef.current) setSuccessMessage(null);
      }, 3000);

    } catch (err) {
      console.error("Failed to initiate post update:", err);
      if (mountedRef.current) {
        setError(t("socialFeed.errors.updatePostInitiation") || "Failed to start post update");
      }
    }
  };

  // Handle local post update (optimistic UI update without server call)
  const handleLocalPostUpdate = (postId, updatedData) => {
    setPosts((prevPosts) =>
      prevPosts.map((post) => {
        if (post.id === postId) {
          // If updatedData is a function, call it with current post
          if (typeof updatedData === 'function') {
            return updatedData(post);
          }
          // Otherwise merge objects
          return { ...post, ...updatedData };
        }
        return post;
      })
    );
  };

  // Handle new repost - add it to the feed
  const handleNewRepost = useCallback((repostData) => {
    if (!repostData || !repostData.data) return;
    
    // Transform the repost data using the same transformation as feed posts
    const transformedRepost = socialUtils.transformPostData(repostData.data);
    if (!transformedRepost) return;

    // Add the new repost to the beginning of the feed
    setPosts((prevPosts) => {
      // Check if repost already exists (prevent duplicates)
      const existingIds = new Set(prevPosts.map(p => String(p.id)));
      if (existingIds.has(String(transformedRepost.id))) {
        return prevPosts;
      }
      
      // Add to beginning and sort by date
      const newPosts = [transformedRepost, ...prevPosts].sort((a, b) => {
        const dateA = new Date(a.created_at || a.timestamp);
        const dateB = new Date(b.created_at || b.timestamp);
        return dateB - dateA;
      });
      
      return newPosts;
    });
  }, []);

  const handleLikeToggle = async (postId) => {
    // Optimistic update
    setPosts((prevPosts) =>
      prevPosts.map((post) => {
        if (post.id === postId) {
          const isLiked = !post.is_liked;
          return {
            ...post,
            is_liked: isLiked,
            reactions_count: isLiked
              ? (post.reactions_count || 0) + 1
              : Math.max((post.reactions_count || 0) - 1, 0),
          };
        }
        return post;
      })
    );

    try {
      await postsAPI.toggleLike(postId);
    } catch (err) {
      // Revert if failed
      console.error("Failed to toggle like:", err);
      setPosts((prevPosts) =>
        prevPosts.map((post) => {
          if (post.id === postId) {
            const isLiked = !post.is_liked; // Revert to previous state
            return {
              ...post,
              is_liked: isLiked,
              reactions_count: isLiked
                ? (post.reactions_count || 0) + 1
                : Math.max((post.reactions_count || 0) - 1, 0),
            };
          }
          return post;
        })
      );
    }
  };

  const handleCommentAdded = (postId, newComment) => {
    setPosts((prevPosts) =>
      prevPosts.map((post) =>
        post.id === postId
          ? { ...post, comments_count: (post.comments_count || 0) + 1 }
          : post
      )
    );
  };

  const loadMorePosts = () => {
    if (!isLoading && hasMorePosts) {
      const nextPage = currentPage + 1;
      setCurrentPage(nextPage);
      loadConsolidatedFeed(nextPage, true);
    }
  };

  const openCreatePostModal = (type = null) => {
    setPendingFileType(type);
    setEditingPostData(null);
    setIsPostModalOpen(true);
  };

  const openEditPostModal = useCallback((post) => {
    setEditingPostData(post);
    setPendingFileType(null);
    setIsPostModalOpen(true); // Reusing the same modal state
  }, []);

  const closePostModal = () => {
    setIsPostModalOpen(false);
    setEditingPostData(null);
    setPendingFileType(null);
  };

  // Delete post handlers
  const openDeleteModal = (post) => {
    setPostToDelete(post);
    setDeleteModalOpen(true);
  };

  const handleCancelDelete = () => {
    setDeleteModalOpen(false);
    setPostToDelete(null);
  };

  const handleConfirmDelete = async () => {
    if (!postToDelete) return;

    setIsDeleting(true);
    try {
      await postsAPI.deletePost(postToDelete.id);

      // Remove post from local state
      setPosts(prevPosts => prevPosts.filter(p => p.id !== postToDelete.id));

      setSuccessMessage(t("socialFeed.success.postDeleted"));
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err) {
      console.error("Failed to delete post:", err);
      setError(t("socialFeed.errors.deletePost"));
    } finally {
      setIsDeleting(false);
      setDeleteModalOpen(false);
      setPostToDelete(null);
    }
  };

  const handleDeletePost = (post) => {
    openDeleteModal(post);
  };

  const sessionExpiredMessage = t("socialFeed.errors.sessionExpired");
  const authRequiredMessage = t("socialFeed.errors.authenticationRequired");
  const isAuthError =
    typeof error === "string" &&
    (error === sessionExpiredMessage ||
      error === authRequiredMessage ||
      error.includes("Session expired") ||
      error.includes("Authentication required"));

  if (isAuthError) {
    // Render nothing while redirecting or show error above
  }

  const handleRetry = () => {
    setError(null);
    setIsLoading(true);
    // Reset loadedOnceRef to allow fresh fetch
    loadedOnceRef.current = false;
    loadConsolidatedFeed(1, false);
  };

  if (isLoading && (!posts || posts.length === 0) && !error) {
    return <SocialFeedSkeleton />;
  }

  return (
    <div className="social-root" style={{ backgroundColor: '#FEFEFB', minHeight: '100vh' }}>
      {/* Error message with retry option */}
      {error && (
        <div className="social-error-toast">
          <div className="social-error-content">
            <span>{error}</span>
            <div>
              {isAuthError && (
                <button
                  onClick={() => {
                    if (!redirectedRef.current) {
                      redirectedRef.current = true;
                      navigate("/auth/login", { replace: true });
                    }
                  }}
                  className="social-error-btn"
                >
                  {t("socialFeed.buttons.login")}
                </button>
              )}
              {!isAuthError && (
                <button
                  onClick={handleRetry}
                  className="social-error-btn"
                >
                  {t("socialFeed.buttons.retry")}
                </button>
              )}
              <button
                onClick={() => setError(null)}
                className="social-error-close"
              >
                ×
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Success message */}
      {successMessage && (
        <div className="social-success-toast">
          <div className="social-success-content">
            <span>{successMessage}</span>
            <button
              onClick={() => setSuccessMessage(null)}
              className="social-success-close"
            >
              ×
            </button>
          </div>
        </div>
      )}

      {/* Video Processing Banner */}
      {isProcessingVideo && (
        <div
          style={{
            margin: '0 16px 16px 16px',
            padding: '12px 16px',
            backgroundColor: '#fffbeb',
            border: '1px solid #fcd34d',
            borderRadius: '12px',
            color: '#92400e',
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            animation: 'fadeIn 0.3s ease-in-out'
          }}
        >
          <div style={{
            backgroundColor: '#fbbf24',
            padding: '4px',
            borderRadius: '50%'
          }}>
            <div style={{
              width: '8px',
              height: '8px',
              backgroundColor: '#fff',
              borderRadius: '50%',
              animation: 'pulse 2s infinite'
            }} />
          </div>
          <span style={{ fontWeight: 500, fontSize: '14px' }}>
            {t('socialFeed.videoProcessing.banner')}
          </span>
        </div>
      )}

      <div className="social-feed-grid">
        <div className="social-feed-body">
          <aside className="social-sidebar social-sidebar-left">
            <div className="sidebar-sticky-wrapper">
              <SocialSidebar currentUser={currentUser} userType={getUserType()} />
            </div>
          </aside>

          <main className="social-main-area">
            <div className="social-feed-stories">
              {isLoadingStories ? (
                <div className="social-stories-loading">
                  <div className="social-stories-loading-content">
                    <div className="social-stories-spinner"></div>
                    <span className="social-stories-loading-text">
                      {t("socialFeed.loading.stories")}
                    </span>
                  </div>
                </div>
              ) : (
                <StoriesCarousel
                  stories={stories}
                  currentUser={currentUser}
                  onStoryAdded={() => loadConsolidatedFeed(1, false)}
                  onStoryDeleted={() => loadConsolidatedFeed(1, false)}
                  viewedStoryIds={viewedStoryIds}
                  onStoryViewed={(viewedIds) => {
                    // Update local state with newly viewed story IDs
                    setViewedStoryIds(prev => {
                      const newSet = new Set(prev);
                      viewedIds.forEach(id => {
                        newSet.add(String(id));
                      });
                      return newSet;
                    });
                  }}
                />
              )}
            </div>

            {/* Create Post Button */}
            <div
              style={{
                background: "white",
                borderRadius: "12px",
                padding: "16px",
                marginBottom: "20px",
                boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                cursor: "pointer",
                transition: "all 0.2s ease",
              }}
              onClick={() => openCreatePostModal()}
              onMouseEnter={(e) => {
                e.currentTarget.style.boxShadow = "0 4px 8px rgba(0,0,0,0.15)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.boxShadow = "0 2px 4px rgba(0,0,0,0.1)";
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "12px",
                }}
              >
                <img
                  src={
                    getProfileImageUrl() ||
                    getUserPlaceholderImage(currentUser)
                  }
                  alt={currentUser?.name || "Profile"}
                  style={{
                    width: "48px",
                    height: "48px",
                    borderRadius: "50%",
                    objectFit: "cover",
                  }}
                  onError={(e) => {
                    e.target.src = getUserPlaceholderImage(currentUser);
                  }}
                />
                <div
                  style={{
                    flex: 1,
                    padding: "12px 16px",
                    border: "1px solid #e5e7eb",
                    borderRadius: "24px",
                    color: "#6b7280",
                    fontSize: "16px",
                  }}
                >
                  {t("postModal.createPlaceholder") || "What's on your mind?"}
                </div>
              </div>

              {/* Photo and Video Buttons */}
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "12px",
                  marginTop: "12px",
                  paddingTop: "12px",
                  borderTop: "1px solid #e5e7eb",
                }}
                onClick={(e) => e.stopPropagation()}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "6px",
                    color: "#494949",
                    fontSize: "14px",
                    fontWeight: 500,
                    cursor: "pointer",
                    transition: "color 0.2s ease",
                    padding: "6px 12px",
                    borderRadius: "8px",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.color = "#3b82f6";
                    e.currentTarget.style.background = "#f3f4f6";
                    const svg = e.currentTarget.querySelector("svg");
                    if (svg) svg.style.stroke = "#3b82f6";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.color = "#494949";
                    e.currentTarget.style.background = "transparent";
                    const svg = e.currentTarget.querySelector("svg");
                    if (svg) svg.style.stroke = "#494949";
                  }}
                  onClick={() => openCreatePostModal("image")}
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#3b82f6" strokeWidth="2">
                    <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                    <circle cx="8.5" cy="8.5" r="1.5" />
                    <polyline points="21 15 16 10 5 21" />
                  </svg>
                  <span style={{ color: "#3b82f6" }}>{t("postCreation.addImage") || "Photo"}</span>
                </div>

                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "6px",
                    color: "#494949",
                    fontSize: "14px",
                    fontWeight: 500,
                    cursor: "pointer",
                    transition: "color 0.2s ease",
                    padding: "6px 12px",
                    borderRadius: "8px",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.color = "#10b981";
                    e.currentTarget.style.background = "#f3f4f6";
                    const svg = e.currentTarget.querySelector("svg");
                    if (svg) svg.style.stroke = "#10b981";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.color = "#494949";
                    e.currentTarget.style.background = "transparent";
                    const svg = e.currentTarget.querySelector("svg");
                    if (svg) svg.style.stroke = "#494949";
                  }}
                  onClick={() => openCreatePostModal("video")}
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#10b981" strokeWidth="2">
                    <polygon points="23 7 16 12 23 17 23 7" />
                    <rect x="1" y="5" width="15" height="14" rx="2" ry="2" />
                  </svg>
                  <span style={{ color: "#10b981" }}>{t("postCreation.addVideo") || "Video"}</span>
                </div>
              </div>
            </div>

            <PostFeed
              posts={posts}
              currentUser={currentUser}
              onUpdatePost={handleLocalPostUpdate}
              onDeletePost={handleDeletePost}
              onLikeToggle={handleLikeToggle}
              onCommentAdded={handleCommentAdded}
              onLoadMore={loadMorePosts}
              hasMorePosts={hasMorePosts}
              isLoading={isLoading}
              onEditPost={openEditPostModal}
              onNewRepost={handleNewRepost}
            />

            {!isLoading && posts && posts.length === 0 && !error && (
              <div className="empty-state">
                <h3>{t("socialFeed.empty.title")}</h3>
                <p>{t("socialFeed.empty.description")}</p>
              </div>
            )}
          </main>

          <aside className="social-sidebar social-sidebar-right">
            <div className="sidebar-sticky-wrapper">
              <RightSidebar suggestedConnections={suggestedConnections} />
            </div>
          </aside>
        </div>
      </div>

      <DeleteConfirmationModal
        isOpen={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        onConfirm={handleConfirmDelete}
        title={t("posts.deleteConfirm.title")}
        message={t("posts.deleteConfirm.message")}
        isDeleting={isDeleting}
      />

      {/* System Announcement Modal */}
      <AnnouncementModal
        isOpen={isAnnouncementModalOpen}
        onClose={() => setIsAnnouncementModalOpen(false)}
        announcement={announcement}
      />

      <PostModal
        isOpen={isPostModalOpen}
        onClose={closePostModal}
        mode={editingPostData ? "edit" : "create"}
        editingPost={editingPostData}
        currentUser={currentUser}
        onCreatePost={handleCreatePost}
        onUpdatePost={handleUpdatePost}
        initialPrivacy={selectedPrivacy}
        pendingFileType={pendingFileType}
      />
    </div>
  );
};


export default SocialFeed;
